/**
 * Note Checklist Item Component
 * 
 * Renders a checklist item with toggle and promote-to-task action
 * Apple-like simplicity with smooth animations
 */

import React from 'react';
import { NoteChecklistItem as ChecklistItemType } from '@/types/notepad';
import { useUpdateNoteChecklist, usePromoteChecklistItemToTask } from '@/hooks/useMyNotes';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Timestamp } from 'firebase/firestore';

interface NoteChecklistItemProps {
  noteId: string;
  item: ChecklistItemType;
  allItems: ChecklistItemType[];
}

export function NoteChecklistItem({ noteId, item, allItems }: NoteChecklistItemProps) {
  const updateChecklist = useUpdateNoteChecklist();
  const promoteToTask = usePromoteChecklistItemToTask();

  const handleToggle = () => {
    const updatedItems = allItems.map((i) =>
      i.id === item.id
        ? {
            ...i,
            done: !i.done,
            doneAt: !i.done ? Timestamp.fromDate(new Date()) : null,
          }
        : i
    );

    updateChecklist.mutate({
      noteId,
      checklist: updatedItems,
    });
  };

  const handlePromote = () => {
    promoteToTask.mutate({
      noteId,
      itemId: item.id,
    });
  };

  return (
    <div
      className={cn(
        'group flex items-start gap-3 p-2 rounded-md transition-colors hover:bg-muted/50',
        item.done && 'opacity-60'
      )}
    >
      {/* Checkbox */}
      <Checkbox
        checked={item.done}
        onCheckedChange={handleToggle}
        className="mt-0.5"
        aria-label={`Mark "${item.text}" as ${item.done ? 'not done' : 'done'}`}
      />

      {/* Text */}
      <span
        className={cn(
          'flex-1 text-sm leading-relaxed',
          item.done && 'line-through text-muted-foreground'
        )}
      >
        {item.text}
      </span>

      {/* Promote to Task button */}
      {!item.done && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePromote}
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
          aria-label={`Convert "${item.text}" to task`}
          title="Convert to task"
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

/**
 * Checklist container component
 */
interface NoteChecklistProps {
  noteId: string;
  items: ChecklistItemType[];
}

export function NoteChecklist({ noteId, items }: NoteChecklistProps) {
  if (!items || items.length === 0) {
    return null;
  }

  const completedCount = items.filter((item) => item.done).length;
  const totalCount = items.length;

  return (
    <div className="space-y-1">
      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(completedCount / totalCount) * 100}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground font-medium">
          {completedCount}/{totalCount}
        </span>
      </div>

      {/* Checklist items */}
      <div className="space-y-0.5">
        {items.map((item) => (
          <NoteChecklistItem
            key={item.id}
            noteId={noteId}
            item={item}
            allItems={items}
          />
        ))}
      </div>
    </div>
  );
}
